import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, User, ArrowRight } from 'lucide-react';

function OrderDeliveryPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden pt-12"> {/* Added pt-12 for padding top */}
      {/* Background with Animated Neon Lines and Shapes */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#fbfbfd] to-white">
        {/* Rest of the background code remains the same */}
        <div className="absolute inset-0 animate-grid-fade" style={{
          backgroundImage: `
            linear-gradient(90deg, rgba(1, 113, 227, 0.03) 1px, transparent 1px),
            linear-gradient(0deg, rgba(1, 113, 227, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }} />

        {/* Animated Neon Lines */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Existing animated lines remain the same */}
          <div className="absolute top-1/4 -left-10 right-0 h-[1px] bg-[#0171E3] animate-float-slow"
               style={{
                 boxShadow: '0 0 10px #0171E3, 0 0 20px #0171E3',
                 opacity: 0.3,
                 transform: 'rotate(-5deg)'
               }} />
          
          <div className="absolute top-0 bottom-0 right-1/3 w-[1px] bg-[#0171E3] animate-pulse-line"
               style={{
                 boxShadow: '0 0 10px #0171E3, 0 0 20px #0171E3',
                 opacity: 0.2,
                 transform: 'rotate(5deg)'
               }} />

          <div className="absolute top-0 -left-1/4 w-full h-[1px] bg-[#0171E3] animate-slide"
               style={{
                 boxShadow: '0 0 10px #0171E3, 0 0 20px #0171E3',
                 opacity: 0.15,
                 transform: 'rotate(45deg)'
               }} />
        </div>

        {/* Rest of the existing background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Existing shapes remain the same */}
          <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full border border-[#0171E3]/20 animate-spin-slow"
               style={{
                 boxShadow: '0 0 30px rgba(1, 113, 227, 0.1)',
                 transform: 'translate(-50%, -50%)'
               }} />

          <div className="absolute bottom-1/3 right-1/4 w-24 h-24 border border-[#0171E3]/10 animate-float"
               style={{
                 boxShadow: '0 0 30px rgba(1, 113, 227, 0.05)',
                 transform: 'rotate(45deg)'
               }} />

          <div className="absolute top-2/3 left-1/3 animate-pulse-scale"
               style={{
                 width: 0,
                 height: 0,
                 borderLeft: '25px solid transparent',
                 borderRight: '25px solid transparent',
                 borderBottom: '40px solid rgba(1, 113, 227, 0.05)',
                 filter: 'drop-shadow(0 0 20px rgba(1, 113, 227, 0.1))'
               }} />
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-transparent to-[#0171E3]/5 animate-gradient" />
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col md:flex-row relative z-10">
        {/* Rest of the component remains the same */}
        {/* Business Section */}
        <div className="flex-1 relative group cursor-pointer overflow-hidden"
             onClick={() => navigate('/business-delivery')}>
          {/* Business section content remains the same */}
        </div>

        {/* Divider */}
        <div className="hidden md:block w-px mx-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0171E3]/10 to-transparent animate-pulse-opacity"
               style={{
                 boxShadow: '0 0 10px rgba(1, 113, 227, 0.2)'
               }} />
        </div>

        {/* Personal Section */}
        <div className="flex-1 relative group cursor-pointer overflow-hidden"
             onClick={() => navigate('/personal-delivery')}>
          {/* Personal section content remains the same */}
        </div>
      </div>

      {/* Existing styles remain the same */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(45deg); }
          50% { transform: translateY(-20px) rotate(45deg); }
        }

        @keyframes float-slow {
          0%, 100% { transform: translateY(0) rotate(-5deg); }
          50% { transform: translateY(-10px) rotate(-5deg); }
        }

        @keyframes pulse-line {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.4; }
        }

        @keyframes pulse-opacity {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }

        @keyframes pulse-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @keyframes slide {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(100%) rotate(45deg); }
        }

        @keyframes grid-fade {
          0%, 100% { opacity: 0.03; }
          50% { opacity: 0.06; }
        }

        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        .animate-pulse-line { animation: pulse-line 4s ease-in-out infinite; }
        .animate-pulse-opacity { animation: pulse-opacity 3s ease-in-out infinite; }
        .animate-pulse-scale { animation: pulse-scale 4s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .animate-slide { animation: slide 8s linear infinite; }
        .animate-grid-fade { animation: grid-fade 4s ease-in-out infinite; }
        .animate-gradient { 
          background-size: 200% 200%;
          animation: gradient 8s ease infinite;
        }

        @media (max-width: 768px) {
          .min-h-screen {
            min-height: 50vh;
          }
        }
      `}</style>
    </div>
  );
}

export default OrderDeliveryPage;